import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { AccountGroup, AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { AddressService } from '@vendasta/businesses';
import { Observable, ReplaySubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-address-cell',
  template: `
    <div class="title">
      <a [routerLink]="url$ | async">{{ companyName$ | async }}</a>
    </div>
    <div class="subtitle">{{ address$ | async }}</div>
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        min-width: 0;
        max-width: 100%;
      }

      .title {
        font-size: 14px;
        font-weight: 500;
      }

      .subtitle {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 14px;
        color: rgba(0, 0, 0, 0.54);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AddressCellComponent {
  private accountGroup$$ = new ReplaySubject<AccountGroup>(1);

  url$: Observable<string[]>;
  companyName$: Observable<string>;
  address$: Observable<string>;

  @Input() set config(value: { accountGroupId?: string; accountGroup?: AccountGroup }) {
    if (value.accountGroup) {
      this.accountGroup = value.accountGroup;
    } else if (value.accountGroupId) {
      this.accountGroupId = value.accountGroupId;
    }
  }

  @Input() set accountGroup(value: AccountGroup) {
    this.accountGroup$$.next(value);
  }

  @Input() set accountGroupId(value: string) {
    this.accountGroupService
      .get(value, new ProjectionFilter({ napData: true }))
      .subscribe((ag) => (this.accountGroup = ag));
  }

  constructor(
    private addressService: AddressService,
    private accountGroupService: AccountGroupService,
  ) {
    this.url$ = this.accountGroup$$.pipe(
      map((accountGroup) => [`/businesses/accounts/${accountGroup.accountGroupId}/details`]),
    );

    this.companyName$ = this.accountGroup$$.pipe(map((accountGroup) => accountGroup.napData.companyName));

    this.address$ = this.accountGroup$$.pipe(
      switchMap((accountGroup) =>
        this.addressService.shortAddress$({
          address1: accountGroup.napData.address,
          address2: accountGroup.napData.address2,
          city: accountGroup.napData.city,
          province: accountGroup.napData.state,
          zip: accountGroup.napData.zip,
          country: accountGroup.napData.country,
        }),
      ),
    );
  }
}
